<template>
    <section class="offices no-overflow">
        <div class="wrapper">
            <h1 class="h1 mb-48">Офисы и&nbsp;банкоматы</h1>
        </div>
        <ClientOnly>
            <template v-if="allowRender">
                <div class="wrapper">
                    <div class="offices-tabs flex items-start justify-between">
                        <UITabs
                            :tabs="officesModel.tabsData"
                            :current-tab="currentTab"
                            @change="onChangeTabHandler"
                        />
                        <UISwitch
                            :switch-labels="['на карте', 'списком']"
                            class="offices-switcher offices-switcher--desktop"
                            @switch="onSwitchHandler"
                        />
                    </div>
                </div>
                <OfficesControls
                    v-if="regionsList && regionsList.length"
                    ref="officeControlsRef"
                    :regions="regionsList"
                    :type="type"
                    :current-region="currentRegion"
                    :current-city="currentCity"
                    :hide-cities="isCitiesHidden"
                    :active-services="activeServices[type]"
                    @update:region="onRegionUpdateHandler"
                    @update:city="onCityUpdateHandler"
                    @update:services="onServicesUpdateHandler"
                />
                <UISwitch
                    :switch-labels="['на карте', 'списком']"
                    class="offices-switcher offices-switcher--mobile"
                    @switch="onSwitchHandler"
                />
                <div class="offices-search-wrapper wrapper relative">
                    <div class="offices-search mt-32">
                        <UISearch
                            v-click-outside="() => suggestions = []"
                            :placeholder="placeholder"
                            green
                            :input-value="searchValue"
                            :class="{'show-suggestions': allowRenderSuggestions}"
                            @search="onSearchHandler"
                            @search-clean="onClearSearchHandler"
                            @input="onChangeSearchHandler"
                        >
                            <transition name="fade">
                                <UISearchSuggestions
                                    v-if="allowRenderSuggestions"
                                    :suggestions="suggestions"
                                    @change="onSuggestionPickHandler"
                                />
                            </transition>
                        </UISearch>
                    </div>
                </div>
                <OfficesMap
                    v-if="!isSwitchToList"
                    :balloons-data="balloonsData"
                    :coords="currentCoords"
                    :zoom.sync="currentZoom"
                    :type="type"
                    @balloon-open="onOpenBalloonHandler"
                />
                <div
                    v-else
                    class="offices-list wrapper"
                >
                    <template v-if="balloonsData && balloonsData.length">
                        <OfficesListItem
                            v-for="balloonData in officesListsData"
                            :key="balloonData.id"
                            :balloon-data="balloonData"
                            :type="type"
                        />
                        <div
                            v-if="isShowMoreOfficesList"
                            class="flex justify-center w-100"
                        >
                            <UIButton
                                label="показать еще"
                                class="offices-list__more"
                                @click="isShowMore = true"
                            />
                        </div>
                    </template>
                    <p v-else>Ничего не найдено.</p>
                </div>
            </template>
            <div
                v-else
                class="wrapper"
            >
                <h3 class="h3 mt-32">Извините. Сервис временно не доступен!</h3>
            </div>
            <OfficeRemoteRecordingConfirm
                :is-visible="!!remoteRecordingOffice"
                @close="setRemoteRecordingOffice"
                @ok="onRedirectToRecordingPage"
            />
        </ClientOnly>
    </section>
    <!-- <UILoader
        v-else
        :force-loading="isLoading"
    /> -->
</template>

<script>
import {defineComponent, computed, ref, shallowRef, onBeforeMount, watch} from 'vue';
import {UIButton} from '@cibsite/ui-components-vue2';
import {useContext, useRoute, useRouter} from '@nuxtjs/composition-api';

import {useRegionsStore} from '@/stores/regions';
import {useToastsStore} from '@/stores/toasts';
import {useOfficesAtmsTerminalsStore} from '@/stores/offices-atms-terminals';
import * as OfficesModel from '@/core/models/pages/aboutPage/OfficesModel';

import UISwitch from '@/components/UI-components/Switch/UISwitch.vue';
import OfficesControls from '@/components/Pages/About/Offices/OfficesControls.vue';
import UISearch from '@/components/UI-components/UISearch/UISearch.vue';
import OfficesMap from '@/components/Pages/About/Offices/OfficesMap.vue';
import OfficesListItem from '@/components/Pages/About/Offices/OfficesListItem.vue';
import UISearchSuggestions from '@/components/UI-components/UISearch/UISearchSuggestions.vue';
// import UILoader from '@/components/UI-components/Loader/Loader.vue';
import UITabs from '@/components/UI-components/Tabs/UITabs.vue';
import OfficeRemoteRecordingConfirm from '@/components/Pages/About/Offices/OfficeRemoteRecordingConfirm.vue';

const DEFAULT_REGION_TITLE = 'Ростовская область';
const DEFAULT_ZOOM = 12;
const ALL_CITIES_ZOOM = 7;
const CURRENT_OFFICE_ZOOM = 17;
const DEFAULT_OFFICES_LIST_LENGTH = 3;

export default defineComponent({
    name: 'OfficesAndAtms',
    components: {
        UISwitch,
        OfficesControls,
        UISearch,
        OfficesMap,
        OfficesListItem,
        UISearchSuggestions,
        // UILoader,
        UIButton,
        UITabs,
        OfficeRemoteRecordingConfirm,
    },
    setup() {
        const {$isMobileOrTablet} = useContext();
        const route = useRoute();
        const router = useRouter();

        const regionsStore = useRegionsStore();
        // const {getRegionsList} = regionsStore;
        const regionsList = computed(() => regionsStore.regionsList);

        const toastsStore = useToastsStore();
        const {info: toastedInfo} = toastsStore;

        const officesAtmsTerminalsStore = useOfficesAtmsTerminalsStore();
        const {
            setOfficesList,
            setAtmsList,
            setTerminalsList,
            setRemoteRecordingOffice,
            getOfficesList,
            getAtmsList,
            getTerminalsList,
        } = officesAtmsTerminalsStore;

        const officeControlsRef = shallowRef(null);

        const activeTab = ref(0);
        const isSwitchToList = ref(false);
        const isLoading = ref(false);
        const isShowMore = ref(false);
        const isChoosedSuggestion = ref(false);
        const isInit = ref(false);
        const isDisallowUpdate = ref(false);
        const isCitiesHidden = ref(true);

        const currentTab = ref(0);
        const currentRegion = ref(null);
        const currentCity = ref(null);
        const currentCoords = ref([47.229089, 39.719043]);

        const searchValue = ref('');
        const mappedTypeData = ref([]);
        const suggestions = ref([]);
        const currentZoom = ref(ALL_CITIES_ZOOM);
        const activeServices = ref(null);
        const changedServices = ref({
            office: false,
            atm: false,
            terminal: false,
        });

        const remoteRecordingOffice = computed(() => officesAtmsTerminalsStore.remoteRecordingOffice);
        const referenceData = computed(() => {
            return {
                office: officesAtmsTerminalsStore.officesList,
                atm: officesAtmsTerminalsStore.atmsList,
                terminal: officesAtmsTerminalsStore.terminalsList,
            };
        });

        const type = computed(() => {
            return OfficesModel.tabsData[activeTab.value]?.slotName ?? 'office';
        });

        const balloonsData = computed(() => {
            const filtredBallonsData = referenceData.value[type.value].filter((data) => {
                return currentCity.value
                    ? data.city.id === currentCity.value?.id
                    : data.region.id === currentRegion.value?.id;
            });

            if (!activeServices.value?.[type.value].length && !searchValue.value) {
                return filtredBallonsData;
            }

            const resultBalloonData = filtredBallonsData
                .filter((balloonData) => {
                    return activeServices.value[type.value].every((service) => {
                        return balloonData[service];
                    });
                });

            if (!searchValue.value) {
                return resultBalloonData;
            }

            return resultBalloonData.filter((data) => {
                if (data?.address && searchInString(data?.address, searchValue.value)) {
                    return true;
                }
                if (data?.address_full && searchInString(data?.address_full, searchValue.value)) {
                    return true;
                }
                if (data?.title && searchInString(data?.title, searchValue.value)) {
                    return true;
                }

                return false;
            });
        });

        const officesListsData = computed(() => {
            return $isMobileOrTablet && !isShowMore.value
                ? balloonsData.value.slice(0, DEFAULT_OFFICES_LIST_LENGTH)
                : balloonsData.value;
        });

        const isShowMoreOfficesList = computed(() => {
            return !isShowMore.value && $isMobileOrTablet && balloonsData.value.length > DEFAULT_OFFICES_LIST_LENGTH;
        });

        const allowRender = computed(() => {
            return !!currentRegion.value && !!activeServices.value;
        });

        const allowRenderSuggestions = computed(() => {
            return !!suggestions.value?.length;
        });

        const placeholder = computed(() => {
            return $isMobileOrTablet ? 'Введите адрес' : 'Введите адрес или название отделения';
        });

        function stringSanitize(str) {
            return str.toLowerCase().replace(/[^а-яё0-9/]/gi, ' ').split(' ').filter((item) => {
                return !!parseFloat(item) || item.length > 1;
            }).join('');
        }

        function searchInString(string, searchValue) {
            if (searchValue.match(/[a-zA-Z]/)) {
                return false;
            }

            return stringSanitize(string).includes(stringSanitize(searchValue));
        }

        function onSwitchHandler(switchValue) {
            if (isSwitchToList.value === switchValue) {
                return;
            }

            isSwitchToList.value = switchValue;
        }

        async function updateDataList() {
            if (type.value === 'atm' && !referenceData.value.atm.length) {
                return await getAtmsList({region_id: currentRegion.value.id});
            }

            if (type.value === 'terminal' && !referenceData.value.terminal.length) {
                return await getTerminalsList({region_id: currentRegion.value.id});
            }

            if (!referenceData.value?.office?.length) {
                await getOfficesList({payload: {region_id: currentRegion.value.id}});
            }
        }

        function onChangeTabHandler(tab) {
            if (activeTab.value === tab) {
                return;
            }

            activeTab.value = tab;
            searchValue.value = '';

            checkSavedServices();
            updateDataList().then(getMappedTypeData);
        }

        function onSearchHandler(value) {
            searchValue.value = value;
            suggestions.value = [];

            if (!isSwitchToList.value && !balloonsData.value?.length) {
                toastedInfo('По введённым данным ничего не найдено!');
            }
        }

        function onRegionUpdateHandler(region) {
            setToStorage('region', region);
            clearActiveServices();
            currentRegion.value = region;
        }

        function onCityUpdateHandler(city) {
            if (!city && !currentCity.value) {
                return;
            }
            setToStorage('city', city);
            clearActiveServices();
            currentCity.value = city;
        }

        function onServicesUpdateHandler(services) {
            setToStorage('services', services);
            activeServices.value[type.value] = services;

            if (!changedServices.value[type.value]) {
                changedServices.value[type.value] = true;
            }
        }

        function setToStorage(key, value) {
            sessionStorage.setItem(`${key}_${type.value}`, JSON.stringify(value));
        }

        function getFromStorage(key) {
            if (!process.client) {
                return;
            }

            const dataFromStorage = sessionStorage.getItem(`${key}_${type.value}`);

            if (dataFromStorage) {
                return JSON.parse(dataFromStorage);
            }

            return null;
        }

        function clearActiveServices() {
            sessionStorage.removeItem(`services_${type.value}`);
            activeServices.value[type.value] = [];
        }

        function checkSavedServices() {
            const servicesFromStorage = getFromStorage('services');

            if (servicesFromStorage) {
                activeServices.value[type.value] = servicesFromStorage;

                return;
            }

            activeServices.value[type.value] = [];
        }

        function getMappedTypeData() {
            const filtredList = currentCity.value
                ? referenceData.value[type.value].filter((data) => {
                    return data?.city?.id === currentCity.value?.id ?? false;
                })
                : referenceData.value[type.value];

            mappedTypeData.value = filtredList.map((item) => {
                return {
                    address: item.address || item.address_full,
                    title: item.title,
                    id: item.id,
                };
            });
        }

        function onChangeSearchHandler(searchValue) {
            if (searchValue.length < 3) {
                suggestions.value = [];

                return;
            }

            let isTitleSuggestions = false;

            suggestions.value = mappedTypeData.value.filter((typeData) => {
                if (activeServices.value[type.value].length === 0 && changedServices.value[type.value]) {
                    return false;
                }
                if (searchInString(typeData.address, searchValue)) {
                    isTitleSuggestions = false;

                    return true;
                }
                if (typeData?.title && searchInString(typeData.title, searchValue)) {
                    isTitleSuggestions = true;

                    return true;
                }

                return false;
            }).map((item) => {
                return Object.assign({}, item, {label: isTitleSuggestions ? item.title : item.address});
            });
        }

        function onSuggestionPickHandler(suggestion) {
            searchValue.value = suggestion.label;
            suggestions.value = [];
            currentCity.value = null;

            const {city} = referenceData.value[type.value].find((item) => item.id === suggestion.id);

            if (city) {
                currentCity.value = currentRegion.value.cities.find((cityItem) => cityItem.id === city.id);
                isChoosedSuggestion.value = true;
            }
        }

        function onOpenBalloonHandler() {
            if ($isMobileOrTablet) {
                return;
            }

            setTimeout(() => {
                const topScrollPosition = officeControlsRef.value?.$el?.offsetTop;
                topScrollPosition && window.scrollTo({
                    top: topScrollPosition,
                    behavior: 'smooth',
                });
            }, 500);
        }

        function onClearSearchHandler() {
            searchValue.value = '';
            suggestions.value = [];
            currentZoom.value = DEFAULT_ZOOM;
        }

        function onRedirectToRecordingPage() {
            const query = {
                region: remoteRecordingOffice.value.region.id,
                city: remoteRecordingOffice.value.city.id,
                office: remoteRecordingOffice.value.id,
            };

            router.push({
                path: '/for-individuals/deposits/appointment',
                query,
            });

            setRemoteRecordingOffice();
        }

        // async function init() {
        //     try {
        //         isLoading.value = true;
        //         await getRegionsList();
        //         currentRegion.value = regionsList.value.find((region) => region.title === DEFAULT_REGION_TITLE);
        //         await getOfficesList({payload: {region_id: currentRegion.value.id}});
        //     } catch (error) {
        //         console.error(error);
        //     } finally {
        //         isLoading.value = false;
        //     }
        // }

        onBeforeMount(() => {
            isLoading.value = true;
            currentRegion.value = regionsList.value.find((region) => region.title === DEFAULT_REGION_TITLE);
            const [office, atm, terminal] = Object.values(OfficesModel.servicesMap)
                .map((services) => Object.keys(services));

            activeServices.value = {office, atm, terminal};

            checkSavedServices();

            const hash = route.value.hash.slice(1);
            if (hash) {
                currentTab.value = OfficesModel.tabsData.findIndex((tab) => tab.slotName === hash) ?? 0;
                onChangeTabHandler(currentTab.value);
            }
            isLoading.value = false;
        });

        watch(currentCity, (newVal) => {
            !isChoosedSuggestion.value && onClearSearchHandler();

            if (!newVal) {
                currentCoords.value = [currentRegion.value.latitude, currentRegion.value.longitude];
                currentZoom.value = ALL_CITIES_ZOOM;

                return;
            }

            const foundedCity = balloonsData.value?.find((office) => {
                return office.city.id === newVal?.id;
            }) ?? null;

            if (foundedCity) {
                currentCoords.value = [foundedCity.latitude, foundedCity.longitude];
                currentZoom.value = isChoosedSuggestion.value ? CURRENT_OFFICE_ZOOM : DEFAULT_ZOOM;
                !isDisallowUpdate.value && getMappedTypeData();
            }

            isChoosedSuggestion.value = false;
            isDisallowUpdate.value = false;
        }, {deep: true});

        watch(currentRegion, (newValue) => {
            if (!newValue && !isInit.value) {
                return;
            }

            isInit.value = true;

            searchValue.value && onClearSearchHandler();
            setOfficesList();
            setAtmsList();
            setTerminalsList();
            isCitiesHidden.value = false;

            updateDataList()
                .then(() => {
                    if (newValue.cities.length === 1) {
                        isDisallowUpdate.value = true;
                        [this.currentCity] = newValue.cities;
                        isCitiesHidden.value = true;

                        return;
                    }

                    currentCity.value = null;
                    currentCoords.value = [newValue.latitude, newValue.longitude];
                    currentZoom.value = ALL_CITIES_ZOOM;
                })
                .then(getMappedTypeData);
        }, {deep: true});

        return {
            officesModel: OfficesModel,
            officeControlsRef,
            regionsList,
            remoteRecordingOffice,
            currentTab,
            isSwitchToList,
            isCitiesHidden,
            currentRegion,
            currentCity,
            activeServices,
            searchValue,
            isLoading,
            currentCoords,
            currentZoom,
            suggestions,
            isShowMore,
            type,
            balloonsData,
            officesListsData,
            isShowMoreOfficesList,
            allowRender,
            allowRenderSuggestions,
            placeholder,
            setRemoteRecordingOffice,
            onSwitchHandler,
            onChangeTabHandler,
            onSearchHandler,
            onRegionUpdateHandler,
            onCityUpdateHandler,
            onServicesUpdateHandler,
            onChangeSearchHandler,
            onSuggestionPickHandler,
            onOpenBalloonHandler,
            onClearSearchHandler,
            onRedirectToRecordingPage,
        };
    },
    async asyncData() {
        const regionsStore = useRegionsStore();
        const officesAtmsTerminalsStore = useOfficesAtmsTerminalsStore();

        if (!regionsStore.regionsList.length) {
            await regionsStore.getRegionsList();
        }
        const currentRegion = regionsStore.regionsList.find((region) => region.title === DEFAULT_REGION_TITLE);
        await officesAtmsTerminalsStore.getOfficesList({payload: {region_id: currentRegion.id}});

        return {};
    },
});
</script>

<style lang="scss">
@import '@/assets/styles/pages/about/offices';
</style>
